import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import {ReactComponent as SendEmailSvg} from "../../../../assets/images/art-like/send-email.svg";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import ToastService from "../../../../core/services/toast";
import {MuiRFF, Yup} from '../../form';
import LoadingLottieAnimation from "../lottie-animations/loading";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";

export const SendOrderEmailTextTypes = {
    payment: 'payment',
    orderInfo: 'orderInfo',
}


const schema = Yup.object().shape({
    email: Yup.string().email(ValidateMessages.incorrectType('valid email address')).nullable().required(ValidateMessages.required),
    includeBcc: Yup.boolean().nullable(),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const SendOrderEmail = ({
                            submit: submitProps,
                            formId,
                            textType = SendOrderEmailTextTypes.orderInfo,
                            customerId,
                        }) => {
    const [loadingCustomerAdminEmail, setLoadingCustomerAdminEmail] = useState(false);
    const [customerAdminEmail, setCustomerAdminEmail] = useState('');
    const {getValue} = usePartnerConfiguration();
    const isLayoutMounted = useIsMounted(true);

    const fetchCustomerAdminInfo = useCallback(async () => {
        setLoadingCustomerAdminEmail(true);
        const response = await Api.getUserAdminByCustomerId(customerId);
        if (!isLayoutMounted())
            return;
        if (response?.resultFlag) {
            setCustomerAdminEmail(response?.data?.email ?? "");
        }
        setLoadingCustomerAdminEmail(false);
    }, [customerId, isLayoutMounted])

    /**
     * After the component is mounted, fetches the customer admin info if the customerId is provided.
     */
    useLayoutEffect(() => {
        if (!!customerId) {
            void fetchCustomerAdminInfo();
        }
    }, [customerId, fetchCustomerAdminInfo]);

    const initialValues = useMemo(() => ({
        email: customerAdminEmail,
        includeBcc: getValue((ids) => ids.sendBccOfInvoicesEmailToOperator) ?? false,
    }), [customerAdminEmail, getValue]);

    const text = useMemo(() => {
        switch (textType) {
            case SendOrderEmailTextTypes.orderInfo:
            default:
                return "Do you want to send this order information to the specified email address?"
            case SendOrderEmailTextTypes.payment:
                return "Your customers' payment has been successfully issued. Would you like to send them a" +
                    " confirmation email?"
        }
    }, [textType])

    const submit = useCallback(async (values) => {
        if (loadingCustomerAdminEmail) {
            ToastService.warning({
                title: 'Please wait',
                message: 'We are still fetching the customer admin email address.',
                props: {
                    toastId: 'fetching-customer-admin-email',
                },
            })
            return;
        }
        if (submitProps) {
            await submitProps(values);
        }
    }, [submitProps, loadingCustomerAdminEmail]);

    return (
        <>
            {
                loadingCustomerAdminEmail &&
                <LoadingLottieAnimation width={'80%'}/>
            }
            {
                !loadingCustomerAdminEmail &&
                <Form
                    id={formId}
                    className='w-100'
                    onSubmit={submit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Col xs={12} className={'mb-3 h-100 my-4 w-100 align-items-center justify-content-center d-flex flex-column'}>
                                    <SendEmailSvg className={'svg'} style={{width: "min(300px, 80vw)"}}/>
                                    <p className='text-sm text-color-1 mt-5 px-4 align-self-start'>
                                        {text}
                                    </p>
                                </Col>
                                <Row className='no-gutters justify-content-around w-100 px-4'>
                                    <Col xs={12} className={'mb-3'}>
                                        <MuiRFF.TextField
                                            label={'Email'}
                                            name={'email'}
                                            type={'email'}
                                            required={required.email}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-2'}>
                                        <CheckboxForm
                                            name={'includeBcc'}
                                            formControlLabelProps={{sx: {margin: 0,}}}
                                            required={required.includeBcc}
                                            data={{
                                                value: values.includeBcc, label: 'Send a copy to me as BCC'
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Row>
                        );
                    }}
                />
            }
        </>
    )
}

export default SendOrderEmail;
